import { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Grid, TextField, Stack, Button, IconButton, List, ListItem, ListItemText } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import { useLanguage } from '../../../utils/i18n';
import { getPolygonTypeById, createPolygonType, updatePolygonType } from '../../../stores/polygonTypeStore';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { baseMaps } from '../../../utils/mapConfig';
import LayersIcon from '@mui/icons-material/Layers';

const hexToRgb = (hex) => {
  if (!hex) return '';
  let r = 0,
    g = 0,
    b = 0;
  const cleanHex = hex.replace('#', '');
  if (cleanHex.length === 6) {
    r = parseInt(cleanHex.slice(0, 2), 16);
    g = parseInt(cleanHex.slice(2, 4), 16);
    b = parseInt(cleanHex.slice(4, 6), 16);
    return `RGB(${r}, ${g}, ${b})`;
  }
  return '';
};

const PolygonTypeAddEdit = ({ open, id, onClose, onSuccess }) => {
  const [initialValues, setInitialValues] = useState({
    name: '',
    description: '',
    fill_color: '#485D9A',
    outline_color: '#485D9A'
  });
  const { t } = useLanguage();

  const [loading, setLoading] = useState(false);
  const notify = useNotifier();

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetchInitial = async () => {
      setLoading(true);
      try {
        if (id) {
          const data = await getPolygonTypeById(id);
          if (!mounted) return;
          if (data) {
            setInitialValues({
              name: data.name ?? '',
              description: data.description ?? '',
              fill_color: data.fill_color ?? '#000000',
              outline_color: data.outline_color ?? '#000000'
            });
          }
        } else {
          setInitialValues({
            name: '',
            description: '',
            fill_color: '#485D9A',
            outline_color: '#485D9A'
          });
        }
      } catch (err) {
        console.error('Failed to fetch polygon type:', err);
        notify({ message: t('notify.failedLoadData', 'Failed to load data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchInitial();

    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(t('validation.nameRequired', 'Name is required')),
    description: Yup.string().nullable(),
    fill_color: Yup.string().nullable(),
    outline_color: Yup.string().nullable()
  });

  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          name: String(values.name).toLocaleLowerCase(),
          description: values.description,
          fill_color: values.fill_color,
          outline_color: values.outline_color,
          label: 'geofence'
        };
        let response;
        if (id) {
          response = await updatePolygonType(id, payload);
          notify({ message: t('notify.update', 'Updated successfully'), severity: 'success' });
        } else {
          response = await createPolygonType(payload);
          notify({ message: t('notify.create', 'Created successfully'), severity: 'success' });
        }
        if (response?.code === 400 || response?.status === false || response?.error) {
          throw response;
        }
        formik.resetForm();
        if (onSuccess) onSuccess();
        else if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage =
          err?.response?.data?.message || err?.data?.message || err?.message || t('notify.failedSave', 'Failed to save data');
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  const mapRef = useRef(null);
  const polygonLayerRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  const [activeBaseMap, setActiveBaseMap] = useState('Google Map');
  const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false);

  const handleBaseMapChange = (mapName) => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    Object.values(baseMaps).forEach((layer) => {
      if (map.hasLayer(layer)) {
        map.removeLayer(layer);
      }
    });

    if (baseMaps[mapName]) {
      baseMaps[mapName].addTo(map);
    }
    setActiveBaseMap(mapName);
    setIsLayerMenuOpen(false);
  };

  useEffect(() => {
    if (!open) {
      setMapReady(false);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        polygonLayerRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    if (!open || loading) {
      return;
    }

    const timer = setTimeout(() => {
      const container = document.getElementById('polygon_type_preview_map');
      if (!container) return;

      if (!mapRef.current) {
        const defaultLayer = baseMaps['Google Map'] || Object.values(baseMaps)[0];
        const map = L.map(container, {
          center: [-6.204, 106.818],
          zoom: 15,
          zoomControl: false,
          attributionControl: false,
          layers: [defaultLayer]
        });
        mapRef.current = map;

        const MOCK_POLYGON = [
          [-6.2025, 106.816],
          [-6.2055, 106.816],
          [-6.2055, 106.82],
          [-6.2025, 106.82]
        ];

        const polygon = L.polygon(MOCK_POLYGON, {
          color: formik.values.outline_color || '#485D9A',
          fillColor: formik.values.fill_color || '#485D9A',
          fillOpacity: 0.5,
          weight: 2
        }).addTo(map);

        polygonLayerRef.current = polygon;
        map.fitBounds(polygon.getBounds());
        setMapReady(true);
      }
    }, 200);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, loading]); // Run map init when dialog opens and loading finishes

  useEffect(() => {
    if (mapReady && polygonLayerRef.current) {
      polygonLayerRef.current.setStyle({
        color: formik.values.outline_color || '#485D9A',
        fillColor: formik.values.fill_color || '#485D9A'
      });
    }
  }, [formik.values.fill_color, formik.values.outline_color, mapReady]);

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{
          textTransform: 'none',
          borderRadius: 1,
          px: 3,
          fontWeight: 700,
          boxShadow: '0 8px 20px rgba(25,118,210,0.12)'
        }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  const isSpecialData = Boolean(id && (initialValues.name === 'fuel station' || initialValues.name === 'power station'));

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={id ? t('polygonType.editPolygonType', 'Edit Polygon Type') : t('polygonType.addPolygonType', 'Add Polygon Type')}
      maxWidth="md"
      fullHeight={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', maxWidth: 920 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={3}>
              <Grid item size={{ xs: 12, md: 6 }}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    size="medium"
                    id="name"
                    name="name"
                    label={t('general.name', 'Name') + ' *'}
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.name && !!formik.errors.name}
                    helperText={formik.touched.name && formik.errors.name}
                    disabled={isSpecialData}
                  />

                  <TextField
                    fullWidth
                    size="medium"
                    id="description"
                    name="description"
                    label={t('general.description', 'Description')}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.description && !!formik.errors.description}
                    helperText={formik.touched.description && formik.errors.description}
                    multiline
                    rows={3}
                  />

                  <Grid container spacing={3}>
                    <Grid item size={{ xs: 6 }}>
                      <TextField
                        fullWidth
                        size="medium"
                        id="fill_color"
                        name="fill_color"
                        label={t('polygonType.fillColorHex', 'Fill Color (HEX)')}
                        type="text"
                        value={formik.values.fill_color}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.fill_color && !!formik.errors.fill_color}
                        helperText={(formik.touched.fill_color && formik.errors.fill_color) || hexToRgb(formik.values.fill_color)}
                        InputProps={{
                          endAdornment: (
                            <input
                              type="color"
                              value={formik.values.fill_color}
                              onChange={(e) => formik.setFieldValue('fill_color', e.target.value.toUpperCase())}
                              style={{ width: 30, height: 30, padding: 0, border: 'none', cursor: 'pointer', background: 'transparent' }}
                            />
                          )
                        }}
                      />
                    </Grid>
                    <Grid item size={{ xs: 6 }}>
                      <TextField
                        fullWidth
                        size="medium"
                        id="outline_color"
                        name="outline_color"
                        label={t('polygonType.outlineColorHex', 'Outline Color (HEX)')}
                        type="text"
                        value={formik.values.outline_color}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.outline_color && !!formik.errors.outline_color}
                        helperText={(formik.touched.outline_color && formik.errors.outline_color) || hexToRgb(formik.values.outline_color)}
                        InputProps={{
                          endAdornment: (
                            <input
                              type="color"
                              value={formik.values.outline_color}
                              onChange={(e) => formik.setFieldValue('outline_color', e.target.value.toUpperCase())}
                              style={{ width: 30, height: 30, padding: 0, border: 'none', cursor: 'pointer', background: 'transparent' }}
                            />
                          )
                        }}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </Grid>
              <Grid item size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: 300,
                    border: '1px solid #ddd',
                    borderRadius: 1,
                    overflow: 'hidden',
                    mt: 1,
                    position: 'relative'
                  }}
                >
                  <div id="polygon_type_preview_map" style={{ width: '100%', height: '100%', minHeight: '300px' }} />

                  {/* Base Map Selector */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      zIndex: 1000
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: 'white',
                        borderRadius: 1,
                        boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
                        p: 0.5,
                        display: 'flex',
                        justifyContent: 'flex-end'
                      }}
                    >
                      <IconButton sx={{ width: 28, height: 28 }} onClick={() => setIsLayerMenuOpen(!isLayerMenuOpen)}>
                        <LayersIcon />
                      </IconButton>
                    </Box>

                    {isLayerMenuOpen && (
                      <Box
                        sx={{
                          bgcolor: 'white',
                          borderRadius: 1,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          p: 1,
                          width: 150,
                          mt: 0.5
                        }}
                      >
                        <List disablePadding>
                          {Object.keys(baseMaps).map((mapName) => {
                            const isActive = mapName === activeBaseMap;
                            return (
                              <ListItem
                                key={mapName}
                                onClick={() => handleBaseMapChange(mapName)}
                                sx={{
                                  p: 0.5,
                                  px: 1.5,
                                  borderRadius: 1,
                                  bgcolor: isActive ? 'rgba(72, 93, 154, 0.15)' : 'transparent',
                                  '&:hover': { bgcolor: 'rgba(72, 93, 154, 0.25)' },
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                <ListItemText
                                  primary={mapName}
                                  primaryTypographyProps={{
                                    fontSize: '0.8rem',
                                    color: isActive ? '#485D9A' : '#333',
                                    fontWeight: isActive ? 'bold' : 'normal'
                                  }}
                                />
                              </ListItem>
                            );
                          })}
                        </List>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default PolygonTypeAddEdit;
